import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Input, Button, Select, message, Spin, Steps, Typography, Space, Alert, Result, Upload, Avatar, Divider } from 'antd';
import { PhoneOutlined, ShopOutlined, UploadOutlined, MailOutlined, EnvironmentOutlined, GlobalOutlined, PlusOutlined } from '@ant-design/icons';
import WA_BUSINESS_CATEGORIES from '../utils/waBusinessCategories';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

/**
 * 新用戶 onboarding 精靈頁面。
 * 路徑: /onboarding-wizard/:companyId
 *
 * 步驟:
 *  0 = 輸入公司名稱 + 電話號碼
 *  1 = 輸入驗證碼（OTP）
 *  2 = 完成
 */
const OnboardingWizardPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [codeMethod, setCodeMethod] = useState('SMS');
  const [verificationCode, setVerificationCode] = useState('');
  const [countdown, setCountdown] = useState(0);

  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [businessCategory, setBusinessCategory] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [website2, setWebsite2] = useState('');

  useEffect(() => {
    if (!companyId) return;
    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/companies/${companyId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setCompanyInfo(data);
          if (data.name) setCompanyName(data.name);
          if (data.description) setDescription(data.description);
          if (data.email) setEmail(data.email);
          if (data.address) setAddress(data.address);
          if (data.website) setWebsite(data.website);
          if (data.website2) setWebsite2(data.website2);
          if (data.wA_BusinessCategory) setBusinessCategory(data.wA_BusinessCategory);
          if (data.logoUrl) setProfileImageUrl(data.logoUrl);
        } else if (res.status === 404 || res.status === 401) {
          message.error('公司帳號已被移除，請重新登入');
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          window.location.href = '/';
        }
      } catch { /* ignore */ }
    };
    fetchCompany();
  }, [companyId]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(prev => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // Step 0 → register phone + request OTP
  const handleSubmitPhone = useCallback(async () => {
    if (!companyName.trim()) { message.error('請輸入公司名稱'); return; }
    if (!phoneNumber.trim()) { message.error('請輸入電話號碼'); return; }

    setLoading(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const profilePayload = {
        ...companyInfo,
        name: companyName.trim(),
        description: description.trim() || companyInfo?.description || '',
        email: email.trim() || companyInfo?.email || '',
        address: address.trim() || companyInfo?.address || '',
        website: website.trim() || companyInfo?.website || '',
        website2: website2.trim() || companyInfo?.website2 || '',
        wA_BusinessCategory: businessCategory || companyInfo?.wA_BusinessCategory || '',
      };

      if (profileImage) {
        const formData = new FormData();
        formData.append('file', profileImage);
        const logoRes = await fetch('/api/companies/logo', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (logoRes.ok) {
          const logoData = await logoRes.json();
          if (logoData.url) {
            profilePayload.logoUrl = logoData.url;
            setProfileImageUrl(logoData.url);
          }
        }
      }

      const updateRes = await fetch(`/api/companies/${companyId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(profilePayload),
      });
      if (updateRes.ok) {
        const updated = await updateRes.json();
        setCompanyInfo(updated);
      }

      const initRes = await fetch('/api/companyphoneverification/onboard-phone', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          companyId,
          phoneNumber: phoneNumber.trim(),
          verifiedName: companyName.trim(),
        }),
      });

      const initData = await initRes.json();
      if (!initRes.ok) {
        if (initRes.status === 404 || initRes.status === 401) {
          message.error('公司帳號已被移除，請重新登入');
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          window.location.href = '/';
          return;
        }
        if (initData.errorType === 'PHONE_ALREADY_ON_WHATSAPP') {
          setErrorMessage(initData.error);
          setErrorType('PHONE_ALREADY_ON_WHATSAPP');
        } else {
          setErrorMessage(initData.error || '初始化電話驗證失敗');
          setErrorType('');
        }
        return;
      }

      const vId = initData.verificationId;
      setVerificationId(vId);

      const codeRes = await fetch(`/api/companyphoneverification/request-verification-code/${vId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phoneNumber: phoneNumber.trim(),
          codeMethod,
          language: 'zh_HK',
        }),
      });

      const codeData = await codeRes.json();
      if (!codeRes.ok) {
        if (codeData.errorType === 'PHONE_ALREADY_ON_WHATSAPP') {
          setErrorMessage(codeData.error);
          setErrorType('PHONE_ALREADY_ON_WHATSAPP');
        } else {
          setErrorMessage(codeData.error || '請求驗證碼失敗');
          setErrorType('');
        }
        return;
      }

      if (codeData.alreadyVerified || codeData.status === 'Linked') {
        setCurrentStep(2);
        message.success('電話號碼已連結完成！');
        return;
      }

      setCountdown(600);
      setCurrentStep(1);
      message.success('驗證碼已發送！');
    } catch (err) {
      setErrorMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [companyId, companyName, phoneNumber, codeMethod, companyInfo, profileImage, businessCategory, description, address, email, website, website2]);

  // Step 1 → verify OTP code
  const handleVerifyCode = useCallback(async () => {
    if (!verificationCode.trim()) { message.error('請輸入驗證碼'); return; }

    setLoading(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`/api/companyphoneverification/verify-code/${verificationId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ code: verificationCode.trim() }),
      });

      const data = await res.json();
      if (!res.ok) {
        setErrorMessage(data.error || '驗證失敗，請檢查驗證碼是否正確');
        return;
      }

      setCurrentStep(2);
      message.success('電話號碼驗證完成！');
    } catch (err) {
      setErrorMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [verificationId, verificationCode]);

  const handleResendCode = useCallback(async () => {
    if (countdown > 0) return;
    setLoading(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`/api/companyphoneverification/request-verification-code/${verificationId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phoneNumber: phoneNumber.trim(),
          codeMethod,
          language: 'zh_HK',
        }),
      });

      if (res.ok) {
        setCountdown(600);
        message.success('驗證碼已重新發送');
      } else {
        const data = await res.json();
        setErrorMessage(data.error || '重新發送失敗');
      }
    } catch (err) {
      setErrorMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [verificationId, phoneNumber, codeMethod, countdown]);

  const handleGoToDashboard = () => {
    try {
      const stored = localStorage.getItem('userInfo');
      if (stored) {
        const info = JSON.parse(stored);
        info.needs_onboarding = false;
        localStorage.setItem('userInfo', JSON.stringify(info));
      }
    } catch { /* ignore */ }
    window.location.href = '/dashboard';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Upload
                showUploadList={false}
                accept="image/jpeg,image/png"
                beforeUpload={(file) => {
                  if (file.size > 5 * 1024 * 1024) {
                    message.error('圖片大小不能超過 5MB');
                    return Upload.LIST_IGNORE;
                  }
                  setProfileImage(file);
                  setProfileImageUrl(URL.createObjectURL(file));
                  return false;
                }}
              >
                <Avatar
                  src={profileImageUrl || undefined}
                  size={96}
                  style={{ cursor: 'pointer', background: '#f0f0f0', marginBottom: 4 }}
                  icon={!profileImageUrl && <PlusOutlined style={{ fontSize: 32, color: '#bbb' }} />}
                />
                <div style={{ color: '#888', fontSize: 13 }}>上傳頭像（選填，建議 640x640）</div>
              </Upload>
            </div>

            <div>
              <Text strong>公司名稱 <Text type="danger">*</Text></Text>
              <Input
                size="large"
                placeholder="請輸入公司名稱"
                prefix={<ShopOutlined />}
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                style={{ marginTop: 8 }}
                status={companyName.trim() ? '' : 'error'}
              />
            </div>

            <div>
              <Text strong>WhatsApp 電話號碼 <Text type="danger">*</Text></Text>
              <Input
                size="large"
                placeholder="+852 9xxx xxxx"
                prefix={<PhoneOutlined />}
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                style={{ marginTop: 8 }}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                請輸入完整國際格式（含國碼），例如 +85291234567
              </Text>
            </div>

            <div>
              <Text strong>行業類別</Text>
              <Select
                value={businessCategory || undefined}
                onChange={setBusinessCategory}
                placeholder="選擇類別"
                style={{ width: '100%', marginTop: 8 }}
                size="large"
                allowClear
              >
                {WA_BUSINESS_CATEGORIES.map(c => (
                  <Option key={c.value} value={c.value}>{c.label}</Option>
                ))}
              </Select>
            </div>

            <div>
              <Text strong>說明</Text>
              <TextArea
                placeholder="簡短描述您的業務（最多 512 字）"
                value={description}
                onChange={e => setDescription(e.target.value)}
                maxLength={512}
                showCount
                rows={3}
                style={{ marginTop: 8 }}
              />
            </div>

            <div>
              <Text strong>地址</Text>
              <Input
                placeholder="輸入商家地址"
                prefix={<EnvironmentOutlined />}
                value={address}
                onChange={e => setAddress(e.target.value)}
                maxLength={256}
                style={{ marginTop: 8 }}
              />
            </div>

            <div>
              <Text strong>電郵</Text>
              <Input
                placeholder="輸入公司電郵"
                prefix={<MailOutlined />}
                value={email}
                onChange={e => setEmail(e.target.value)}
                maxLength={128}
                style={{ marginTop: 8 }}
              />
            </div>

            <div>
              <Text strong>網站</Text>
              <Input
                placeholder="https://www.example.com"
                prefix={<GlobalOutlined />}
                value={website}
                onChange={e => setWebsite(e.target.value)}
                maxLength={256}
                style={{ marginTop: 8 }}
              />
              <Input
                placeholder="https://www.example2.com（選填）"
                prefix={<GlobalOutlined />}
                value={website2}
                onChange={e => setWebsite2(e.target.value)}
                maxLength={256}
                style={{ marginTop: 8 }}
              />
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div>
              <Text strong>驗證碼接收方式</Text>
              <Select
                value={codeMethod}
                onChange={setCodeMethod}
                style={{ width: '100%', marginTop: 8 }}
                size="large"
              >
                <Option value="SMS">SMS 簡訊</Option>
                <Option value="VOICE">語音通話</Option>
              </Select>
            </div>

            <Button type="primary" size="large" block loading={loading} onClick={handleSubmitPhone}>
              註冊電話號碼並發送驗證碼
            </Button>
          </Space>
        );

      case 1:
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              type="info"
              message={`驗證碼已透過 ${codeMethod} 發送至 ${phoneNumber}`}
              description="請在您的手機上查收驗證碼。"
            />
            <div>
              <Text strong>輸入 6 位驗證碼</Text>
              <Input
                size="large"
                placeholder="000000"
                maxLength={6}
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                style={{ marginTop: 8, textAlign: 'center', letterSpacing: 12, fontSize: 24 }}
              />
            </div>
            <Space>
              <Button type="primary" size="large" loading={loading} onClick={handleVerifyCode}>
                驗證
              </Button>
              <Button size="large" disabled={countdown > 0} onClick={handleResendCode}>
                {countdown > 0 ? `重新發送 (${countdown}s)` : '重新發送驗證碼'}
              </Button>
            </Space>
          </Space>
        );

      case 2:
        return (
          <Result
            status="success"
            title="電話號碼設定完成！"
            subTitle={`${phoneNumber} 已成功連結至您的 WhatsApp Business 帳號。`}
            extra={[
              <Button type="primary" key="dashboard" onClick={handleGoToDashboard}>
                前往主頁
              </Button>,
            ]}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: '40px auto', padding: '0 16px' }}>
      <Card>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 32 }}>
          WhatsApp 電話號碼設定精靈
        </Title>

        <Steps
          current={currentStep}
          items={[
            { title: '輸入號碼' },
            { title: '驗證 OTP' },
            { title: '完成' },
          ]}
          style={{ marginBottom: 32 }}
        />

        {errorMessage && (
          <Alert
            type={errorType === 'PHONE_ALREADY_ON_WHATSAPP' ? 'warning' : 'error'}
            message={errorType === 'PHONE_ALREADY_ON_WHATSAPP' ? '電話號碼已有 WhatsApp 帳號' : '錯誤'}
            description={
              errorType === 'PHONE_ALREADY_ON_WHATSAPP' ? (
                <div>
                  <p>{errorMessage}</p>
                  <p style={{ marginTop: 8, fontWeight: 'bold' }}>操作步驟：</p>
                  <ol style={{ paddingLeft: 20, marginTop: 4 }}>
                    <li>打開手機上的 WhatsApp</li>
                    <li>前往「設定」→「帳號」→「刪除我的帳號」</li>
                    <li>輸入電話號碼並確認刪除</li>
                    <li>等待約 5 分鐘</li>
                    <li>回到本頁面重新提交號碼</li>
                  </ol>
                </div>
              ) : errorMessage
            }
            closable
            onClose={() => { setErrorMessage(''); setErrorType(''); }}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

        <Spin spinning={loading && currentStep === 1}>
          {renderStepContent()}
        </Spin>
      </Card>
    </div>
  );
};

export default OnboardingWizardPage;
